import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { depascalize, getEnumValues, listFiltersService, ListTableSort, SortOrder } from "sprint-common-web";

import { CloudFileStatus } from "../../common/types/CloudFileStatus";

export interface CloudFileListingFilters {
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: SortOrder;
    fileName?: string;
    deliveryRef?: string;
    arrivalDateStart?: Date;
    arrivalDateEnd?: Date;
    statuses?: number[];
}

const selectedStatuses = getEnumValues(CloudFileStatus).filter((s) => s !== CloudFileStatus.QcDone);

const defaultFilters: CloudFileListingFilters = {
    page: 1,
    pageSize: 50,
    sortBy: "arrivalDate",
    sortOrder: "desc",
    fileName: "",
    deliveryRef: "",
    arrivalDateStart: undefined,
    arrivalDateEnd: undefined,
    statuses: selectedStatuses
};

export const useCloudFileListingFilters = () => {
    const filtersService = useMemo(() => listFiltersService<CloudFileListingFilters>(defaultFilters), []);
    const location = useLocation();
    const history = useHistory();

    const [filters, setFilters] = useState<CloudFileListingFilters>(() =>
        filtersService.getSavedFilters(location.search)
    );

    useEffect(() => {
        filtersService.saveFilters(location.pathname, history, filters);
    }, [location.pathname, history, filters]);

    const setSort = (sort: ListTableSort) => {
        setFilters((prevFilters: CloudFileListingFilters) => ({
            ...prevFilters,
            sortBy: sort.by,
            sortOrder: sort.order,
            page: 1
        }));
    };

    const setPage = (page: number) => {
        setFilters((prevFilters: CloudFileListingFilters) => ({ ...prevFilters, page }));
    };

    const setPageSize = (pageSize: number): void => {
        setFilters((prevFilters: CloudFileListingFilters) => ({ ...prevFilters, pageSize, page: 1 }));
    };

    const setFileName = (fileName?: string): void => {
        setFilters((prevFilters: CloudFileListingFilters) => ({ ...prevFilters, fileName, page: 1 }));
    };

    const setDeliveryRef = (deliveryRef?: string): void => {
        setFilters((prevFilters: CloudFileListingFilters) => ({ ...prevFilters, deliveryRef, page: 1 }));
    };

    const setArrivalDateStart = (arrivalDateStart?: Date): void => {
        setFilters((prevFilters: CloudFileListingFilters) => ({ ...prevFilters, arrivalDateStart, page: 1 }));
    };

    const setArrivalDateEnd = (arrivalDateEnd?: Date): void => {
        setFilters((prevFilters: CloudFileListingFilters) => ({ ...prevFilters, arrivalDateEnd, page: 1 }));
    };

    const setStatuses = (statuses?: number[]): void => {
        setFilters((prevFilters: CloudFileListingFilters) => ({ ...prevFilters, statuses, page: 1 }));
    };

    const setTypes = (types?: number[]): void => {
        setFilters((prevFilters: CloudFileListingFilters) => ({ ...prevFilters, types, page: 1 }));
    };

    const clearFilters = (): void => {
        setFilters(defaultFilters);
    };

    const statusOptions = selectedStatuses.concat(CloudFileStatus.QcDone).map((status) => ({
        key: status,
        text: depascalize(CloudFileStatus[status])
    }));

    return {
        filters,
        setSort,
        setPage,
        setPageSize,
        setFileName,
        setDeliveryRef,
        setArrivalDateStart,
        setArrivalDateEnd,
        setStatuses,
        setTypes,
        statusOptions,
        clearFilters
    };
};
